<template>
  <div class="sonar-results">
    <div
      ref="resultsContainer"
      class="flat-scroll sonar-results__container"
      :style="`height: ${resultsContainerHeight}px`"
    >
      <div class="sonar-results__wrapper">
        <button class="sonar-results__close-btn" @click="closeResultsModal()">
          <XIcon class="sonar-results__close-icon" />
        </button>
        <SonarResultsScoring
          class="sonar-results__scoring"
          v-bind="{
            currentScore,
            lastAssessment,
          }"
        />
        <div
          v-if="anyAssessmentUnlocked"
          class="sonar-results__next-assessment"
        >
          <h5 class="sonar-results__next-assessment-title">
            New assessment unlocked!
          </h5>
          <SonarAssessmentCard
            v-for="assessment in unlockedAssessments"
            :key="assessment.id"
            :isResultsModal="true"
            :assessment="assessment"
          />
        </div>
        <div v-if="anyRecommendations" class="sonar-results__recommended">
          <h5 class="sonar-results__next-assessment-title">
            Recommended training
          </h5>
          <SonarRecommendedContent
            v-bind="{
              parentIds,
              difficulty,
              recommendedContentItems,
            }"
            :isResultsModal="true"
            :isCtaDisabled="false"
          />
        </div>
        <div
          :class="[
            'sonar-results__footer',
            anyRecommendations && 'sonar-results__footer--thin',
          ]"
        >
          <div class="sonar-results__cta-wrapper">
            <button class="btn btn--primary" @click="closeResultsModal()">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { XIcon } from 'vue-feather-icons'
import SonarResultsScoring from '@/sonar/components/quiz-modal/SonarResultsScoring.vue'
import SonarAssessmentCard from '@/sonar/components/SonarAssessmentCard.vue'
import SonarRecommendedContent from '@/sonar/components/SonarRecommendedContent.vue'

export default {
  name: 'SonarResults',
  components: {
    SonarResultsScoring,
    SonarAssessmentCard,
    SonarRecommendedContent,
    XIcon,
  },
  props: {
    currentScore: {
      type: Object,
      required: false,
    },
    recommendedContentItems: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      resultsContainerHeight: '',
    }
  },
  computed: {
    ...mapGetters({
      lastAssessment: 'sonar/lastAssessment',
    }),
    anyAssessmentUnlocked() {
      return (
        this.lastAssessment?.is_passed &&
        !this.lastAssessment.is_retaken &&
        !!this.lastAssessment?.unlocked_assessments?.length
      )
    },
    anyRecommendations() {
      return this.recommendedContentItems?.some(item => !!item)
    },
    unlockedAssessments() {
      return this.lastAssessment?.unlocked_assessments
    },
    parentIds() {
      return this.lastAssessment?.score_per_skill_area?.map(ska => ska.id)
    },
    difficulty() {
      return this.lastAssessment?.score_per_skill_area[0]?.level
    },
  },
  mounted() {
    this.setContainerHeight()
  },
  methods: {
    setContainerHeight() {
      const height = this.$refs.resultsContainer.clientHeight
      if (!this.anyAssessmentUnlocked)
        return (this.resultsContainerHeight = height)
      const unlockedDefault = 116
      const animationGap = 60
      const assessmentHeight = 88
      const assessmentQty = this.unlockedAssessments.length
      this.resultsContainerHeight =
        height +
        unlockedDefault +
        animationGap +
        assessmentHeight * assessmentQty
    },
    closeResultsModal() {
      this.$emit('close-modal')
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-results {
  position: fixed;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(17.5px);
  top: 0;
  left: 0;
  z-index: 500;
  display: flex;
  justify-content: center;
  align-items: center;
  &__close-btn {
    position: absolute;
    right: 50px;
    top: 30px;
    display: flex;
    justify-content: flex-end;
    background: transparent;
    padding: 0;
    border: none;
    z-index: 200;
    cursor: pointer;
  }
  &__close-icon {
    color: var(--white);
    padding: 0;
  }
  &__container {
    width: 944px;
    max-height: 95vh;
    border-radius: 30px;
    position: relative;
  }
  &__wrapper {
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    background: var(--black);
    border-radius: 30px;
    position: relative;
  }
  &__scoring {
    padding-top: 54px;
  }
  &__next-assessment {
    position: relative;
    z-index: 0;
    overflow: hidden;
    height: 0;
    animation: wiggle 1s 1s ease-in forwards;
    background: var(--black);
    padding: 0 40px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    &-title {
      color: var(--white);
      font-family: var(--font-bold);
      font-size: rem(20);
      line-height: rem(26);
      margin: 0;
    }
    @keyframes wiggle {
      0% {
        padding: 40px;
        margin-bottom: -204px;
      }
      7% {
        height: 100%;
      }
      15% {
        padding: 40px;
      }
      20% {
        margin-bottom: -60px;
      }
      23% {
        margin-bottom: 60px;
      }
      26% {
        margin-bottom: -40px;
      }
      32% {
        margin-bottom: 40px;
      }
      40% {
        margin-bottom: -10px;
      }
      55% {
        margin-bottom: 10px;
      }
      70% {
        margin-bottom: -10px;
      }
      85% {
        margin-bottom: 10px;
      }
      100% {
        padding: 40px;
        margin-bottom: 0;
      }
    }
  }
  &__recommended {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 40px 50px 0;
    background: var(--charcoal);
    z-index: 50;
    ::v-deep .sonar-recommended__container--expand {
      padding: 30px 0 15px;
    }
  }
  &__footer {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding: 40px 50px;
    background: var(--charcoal);
    border-radius: 0 0 30px 30px;
    &--thin {
      padding: 15px 50px 40px;
    }
  }
}
</style>
